// panels/pvc/data.js —— 光储充专属局部演示数据（演示数据，待接口接入）
// 本文件仅属于《云平台单站控制（光储充）需求文档》对应的光储充面板。
// 公共数据层（src/config/stationData.js）已提供设备 / 曲线 / 报表等基础数据，
// 此处只补充公共数据层缺失的光储充演示常量：近七日电量与收益、实时/预测运行曲线、
// 电表本月报表、充电桩充电枪、光伏支路、其他设备（IO/传感器/测控）等。
import {
  strategyPowerAtMin, loadPowerAtMin, pvPowerAtMin,
} from '../../../../config/stationData';

// ---------- 电价（元/kWh） ----------
// 电网电价：系统收益 / 储能收益参考 计算使用；光伏发电电价：光伏收益参考 计算使用。
export const PRICES = { grid: 0.68, pv: 0.42 };

// 各站点演示缩放系数
export const stationScale = (id) => ({ hn: 1, nx: 0.72, yx: 0.5 }[id] || 1);

// 光伏装机功率（kW）——用于「光照出力程度」百分比
export const PV_INSTALL_KW = { hn: 600, nx: 420 };

const pad2 = (n) => String(n).padStart(2, '0');

// ---------- 近七日（不含今天）每日电量与收益 ----------
const DAYS = ['10-10', '10-11', '10-12', '10-13', '10-14', '10-15', '10-16'];

// 每日电量：电网 / 负载 / 光伏 / 储能充电 / 储能放电（kWh）
export const getSevenDayEnergy = (id) => {
  const b = stationScale(id);
  return DAYS.map((day, i) => ({
    day,
    grid: Math.round((4720 + Math.sin(i) * 260) * b),
    load: Math.round((6680 + Math.sin(i + 1) * 320) * b),
    pv: Math.round((1320 + Math.sin(i + 2) * 180) * b),
    charge: Math.round((2140 + Math.sin(i) * 170) * b),
    discharge: Math.round((2280 + Math.sin(i + 1) * 150) * b),
  }));
};

// 每日收益（元）：
// 系统收益 = 负载表电量 × 电网电价 − 电网总表电量 × 电网电价（差异 #1：无系统效率）
// 光伏收益参考 = 光伏发电电量 × 光伏发电电价
// 储能收益参考 = 储能放电量 × 电网电价 − 储能充电量 × 电网电价
export const getSevenDayIncome = (id) => getSevenDayEnergy(id).map((r) => ({
  day: r.day,
  system: Math.round((r.load - r.grid) * PRICES.grid),
  pv: Math.round(r.pv * PRICES.pv),
  storage: Math.round((r.discharge - r.charge) * PRICES.grid),
}));

export const sumField = (rows, key) => rows.reduce((a, r) => a + r[key], 0);

// ---------- 运行曲线：实时数据（今日 0:00~24:00 · 15 分钟） ----------
// 差异 #2：光储充无「今日 0 点 ~ 次日 6 点」时间轴，改为「实时数据 + 预测数据」。
export const getRealtimeCurve = (id) => {
  const b = stationScale(id);
  let soc = 62;
  const pvPeak = (PV_INSTALL_KW[id] || 400) * 0.72;
  return Array.from({ length: 96 }, (_, i) => {
    const h = i / 4;
    const minuteOfDay = Math.round(h * 60);
    const clock = `${pad2(Math.floor(h))}:${pad2((i % 4) * 15)}`;
    const pv = pvPowerAtMin(minuteOfDay, pvPeak, b);
    let charger = 0;
    if (minuteOfDay >= 8 * 60 && minuteOfDay < 12 * 60) charger = Math.round(120 * b);
    else if (minuteOfDay >= 12 * 60 && minuteOfDay < 14 * 60) charger = Math.round(60 * b);
    else if (minuteOfDay >= 14 * 60 && minuteOfDay < 21 * 60) charger = Math.round(180 * b);
    else if (minuteOfDay >= 21 * 60 && minuteOfDay < 23 * 60) charger = Math.round(40 * b);
    const load = loadPowerAtMin(minuteOfDay, b) + charger;
    const storage = Math.round(strategyPowerAtMin(minuteOfDay) * b);
    const grid = load - pv + storage;
    soc = Math.max(20, Math.min(96, soc + storage / (260 * b) * 4));
    return { clock, pv, charger, load, storage, grid, soc: +soc.toFixed(1), demandLimit: Math.round(420 * b) };
  });
};

// ---------- 运行曲线：预测数据（明日 · 15 分钟 · 每日 18:00 更新） ----------
export const getForecastCurve = (id) => {
  const b = stationScale(id);
  const pvPeak = (PV_INSTALL_KW[id] || 400) * 0.78;
  return Array.from({ length: 96 }, (_, i) => {
    const h = i / 4;
    const minuteOfDay = Math.round(h * 60);
    const clock = `${pad2(Math.floor(h))}:${pad2((i % 4) * 15)}`;
    const pvForecast = pvPowerAtMin(minuteOfDay, pvPeak, b);
    const loadForecast = loadPowerAtMin(minuteOfDay, b);
    const strategyPower = Math.round(strategyPowerAtMin(minuteOfDay) * b);
    return { clock, pvForecast, loadForecast, strategyPower };
  });
};

// ---------- 电表报表：本月（不含今日）每日电量（差异 #6） ----------
// 展示：累计正向有功（总/尖/峰/平/谷）、反向有功、无功；计算公式与电量报表一致。
const MONTH_DAYS = Array.from({ length: 16 }, (_, i) => `10-${pad2(i + 1)}`);
const METER_BASE = {
  'M-01': { fp: 1720, sharp: 210, peak: 520, flat: 640, valley: 350, reverse: 0, reactive: 330 },
  'M-02': { fp: 980, sharp: 120, peak: 300, flat: 360, valley: 200, reverse: 0, reactive: 96 },
  'M-03': { fp: 2260, sharp: 280, peak: 690, flat: 820, valley: 470, reverse: 0, reactive: 142 },
  'M-04': { fp: 620, sharp: 70, peak: 190, flat: 220, valley: 140, reverse: 0, reactive: 12 },
};

export const getMeterEnergyMonth = (meterId) => {
  const base = METER_BASE[meterId] || METER_BASE['M-01'];
  return MONTH_DAYS.map((day, i) => {
    const k = 1 + Math.sin(i) * 0.06;
    const fp = Math.round(base.fp * k);
    return {
      key: day,
      day,
      fp,
      sharp: Math.round(base.sharp * k),
      peak: Math.round(base.peak * k),
      flat: Math.round(base.flat * k),
      valley: Math.round(base.valley * k),
      reverse: base.reverse,
      reactive: Math.round(base.reactive * k),
    };
  });
};

// 电表止度：本月每日 00:00 的电表止度（已乘综合倍率后的实际电度）+ 综合倍率
export const METER_RATIO = { 'M-01': 60, 'M-02': 40, 'M-03': 60, 'M-04': 30 };
export const getMeterReadingMonth = (meterId) => {
  const base = 1284.6 + (METER_RATIO[meterId] || 1) * 0;
  let acc = base;
  return MONTH_DAYS.map((day, i) => {
    acc += (METER_BASE[meterId] ? METER_BASE[meterId].fp : 1700) / 1000 * (1 + Math.sin(i) * 0.05);
    return { key: day, day, reading: +acc.toFixed(1), ratio: METER_RATIO[meterId] || 1 };
  });
};

// ---------- 自定义曲线可选数据项（最多选择 10 项 · 差异 #12 全量单体导出最多 12h） ----------
export const CUSTOM_METRICS = [
  { key: 'grid', label: '电网功率', color: '#4dd8ff' },
  { key: 'load', label: '负载功率', color: '#ff5a7f' },
  { key: 'storage', label: '储能功率', color: '#52e8b0' },
  { key: 'pv', label: '光伏功率', color: '#f4c542' },
  { key: 'charger', label: '充电桩功率', color: '#00d4ff' },
  { key: 'soc', label: 'SOC', color: '#8b7cf6' },
  { key: 'demandLimit', label: '需量限制', color: '#ff9f43' },
  // 全量单体：导出采样点多，导出时长受限于 12h
  { key: 'cellVolume', label: '全量单体电压', color: '#e0b0ff', single: true },
  { key: 'cellTemp', label: '全量单体温度', color: '#ffd166', single: true },
];

// ---------- 充电桩充电枪（差异 #11：直连 / 电表采集） ----------
// 直连充电桩可查看每个充电枪的工作状态；电表采集充电桩仅上报电压/电流/功率/充电量。
const GUN_STATES = ['初始状态', '空闲状态', '等待启动', '启动中', '充电中', '停止充电中', '充电结算中', '充电完成未拔枪', '启动失败', '故障状态'];
export const getChargerGuns = (c) => {
  if (c.collect !== '直连') return null;
  return Array.from({ length: c.guns }, (_, g) => {
    const idx = (c.name.charCodeAt(c.name.length - 1) + g) % GUN_STATES.length;
    const state = c.state === '充电中' ? '充电中' : GUN_STATES[idx];
    const charging = state === '充电中';
    return {
      key: `${c.id}-G${g + 1}`,
      gun: `${c.name} · 充电枪 ${String.fromCharCode(65 + g)}`,
      state,
      power: charging ? +(c.power / c.guns).toFixed(1) : 0,
      voltage: charging ? 402 + g : 0,
      current: charging ? +(c.power / c.guns / 0.4).toFixed(1) : 0,
      energy: charging ? +(c.todayEnergy / c.guns).toFixed(1) : 0,
    };
  });
};

// ---------- 光伏逆变器支路数据（差异 #11：含光伏逆变器） ----------
export const getPvBranches = (p) => Array.from({ length: p.mppt }, (_, i) => ({
  key: `${p.id}-MPPT${i + 1}`,
  branch: `MPPT ${i + 1}`,
  state: i === p.mppt - 1 && p.state !== '正常' ? '告警' : '运行',
  voltage: p.dcVoltage + i * 6,
  current: +(p.dcPower / p.mppt / 0.62).toFixed(1),
  power: +(p.dcPower / p.mppt).toFixed(1),
}));

// ---------- 其他设备（3.2.7 IO 控制器 / 传感器 / 测控装置） ----------
export const IO_DEVICES = [
  {
    id: 'IO-01', name: 'IO 控制器 #1', state: '正常',
    di: [['DI1', '消防联动信号', '闭合'], ['DI2', '急停输入', '断开'], ['DI3', '门禁信号', '断开']],
    doPorts: [['DO1', '声光报警', '断开'], ['DO2', '排风扇', '闭合'], ['DO3', '空调联动', '断开']],
  },
  {
    id: 'IO-02', name: 'IO 控制器 #2', state: '正常',
    di: [['DI1', '水浸信号', '断开'], ['DI2', '烟感信号', '断开']],
    doPorts: [['DO1', '消防切非', '断开'], ['DO2', '备用', '断开']],
  },
];

export const SENSORS = [
  { id: 'SEN-01', name: '温湿度传感器 #1', state: '正常', rows: [['温度', '24.8 ℃'], ['湿度', '49.6 %RH'], ['通信状态', '正常']] },
  { id: 'SEN-02', name: '温湿度传感器 #2', state: '正常', rows: [['温度', '25.4 ℃'], ['湿度', '51.2 %RH'], ['通信状态', '正常']] },
  { id: 'SEN-03', name: '水浸传感器 #1', state: '告警', rows: [['漏水状态', '检测到积水'], ['通信状态', '正常']] },
];

export const MEASURES = [
  { id: 'MEA-01', name: '测控装置 #1', state: '正常', rows: [['电压', '398.6 V'], ['电流', '182.4 A'], ['功率', '125.6 kW'], ['通信状态', '正常']] },
  { id: 'MEA-02', name: '测控装置 #2', state: '正常', rows: [['电压', '399.2 V'], ['电流', '176.8 A'], ['功率', '120.4 kW'], ['通信状态', '正常']] },
];

// ---------- 故障告警：AI 翻译可选语言（3.3） ----------
export const LANG_OPTIONS = ['中文', 'English', '日本語', '한국어', 'Deutsch', 'Français'];
// 演示翻译结果（真实环境由后端在线调取 AI 接口）
export const TRANSLATIONS = {
  English: { '电芯温度过高': 'Cell temperature too high', '直流侧绝缘阻抗低': 'DC side insulation resistance low', '单体压差偏大': 'Cell voltage difference too large', '空调通信中断': 'Air conditioner communication interrupted' },
  日本語: { '电芯温度过高': 'セル温度が高すぎます', '直流侧绝缘阻抗低': '直流側絶縁抵抗が低い', '单体压差偏大': 'セル電圧差が大きい', '空调通信中断': '空調通信が中断' },
  한국어: { '电芯温度过高': '셀 온도 과다', '直流侧绝缘阻抗低': 'DC 측 절연 저항 저하', '单体压差偏大': '셀 전압 편차 과대', '空调通信中断': '에어컨 통신 중단' },
  Deutsch: { '电芯温度过高': 'Zelltemperatur zu hoch', '直流侧绝缘阻抗低': 'DC-Isolationswiderstand niedrig', '单体压差偏大': 'Zellspannungsdifferenz zu groß', '空调通信中断': 'Kommunikation der Klimaanlage unterbrochen' },
  Français: { '电芯温度过高': 'Température de cellule trop élevée', '直流侧绝缘阻抗低': 'Résistance d\'isolement DC faible', '单体压差偏大': 'Différence de tension des cellules trop élevée', '空调通信中断': 'Communication du climatiseur interrompue' },
};

// ---------- 控制中心：需量 / 逆功率保护可勾选变压器 ----------
export const TRANSFORMERS = [
  { key: 'T-01', name: '总变压器（与电网对接）', detail: '10/0.4 kV · 2×1250 kVA' },
  { key: 'T-02', name: '储能变压器', detail: '10/0.4 kV · 1×800 kVA' },
];

// ---------- 系统管理：强电结构节点（3.6.2） ----------
export const STRUCT_NODES = [
  { key: 'grid', name: '电网并网点', type: '电网', param: '10 kV 进线 1 路' },
  { key: 'meter', name: '并网柜电表 M-01', type: '电表', param: '综合倍率 60' },
  { key: 'bus', name: '交流母线', type: '母线', param: '0.4 kV' },
  { key: 'pcs', name: 'PCS 储能单元', type: '储能', param: '3 台 / 1.25 MW' },
  { key: 'pv', name: '光伏逆变器', type: '光伏', param: '3 台（柔性可控 2 / 硬性可控 1）' },
  { key: 'charger', name: '充电桩', type: '充电', param: '6 台（直连 3 / 电表采集 3）' },
  { key: 'load', name: '负载', type: '负载', param: '生产负荷 + 充电桩' },
];
